import React, { useMemo, useEffect, useState } from 'react';
import { BlackHoleProps } from '../types';
import { useProcessing } from '../contexts/ProcessingContext';
import { useVault } from '../contexts/VaultContext';

/**
 * Hook to safely get processing context (returns null outside provider)
 */
function useOptionalProcessing() {
  try {
    return useProcessing();
  } catch {
    return null;
  }
}

/**
 * Hook to safely get vault context (returns null outside provider)
 */
function useOptionalVault() {
  try {
    return useVault();
  } catch {
    return null;
  }
}

/**
 * Orbital particle configuration
 */
interface OrbitalParticle {
  id: number;
  angle: number;
  radius: number;
  speed: number;
  size: number;
  opacity: number;
  color: string;
}

/**
 * Data fragment being "sucked in"
 */
interface DataFragment {
  id: number;
  startAngle: number;
  startRadius: number;
  char: string;
  delay: number;
}

/**
 * Orbiting hash configuration
 */
interface OrbitingHashConfig {
  index: number;
  hash: string;
  algorithm: string;
  encrypted: boolean;
  angle: number;
  orbitRadius: number;
  speed: number;
  opacity: number;
}

/**
 * BlackHole component - The centerpiece visualization
 *
 * Features:
 * - Event horizon with gradient and glow effects
 * - Rotating accretion disk
 * - Singularity at center with pulsing effect
 * - Orbiting particles when processing
 * - Data "sucked in" animation during processing
 * - Orbiting hashes from vault displayed on accretion disk
 * - Responsive sizing
 * - Configurable intensity
 */
export const BlackHole: React.FC<BlackHoleProps> = ({
  isProcessing: isProcessingProp,
  intensity = 0.5,
  size = 320,
  orbitingHashes: orbitingHashesProp,
}) => {
  const [dataFragments, setDataFragments] = useState<DataFragment[]>([]);

  // Get context values (null if outside provider)
  const processingContext = useOptionalProcessing();
  const vaultContext = useOptionalVault();

  // Use prop if provided, otherwise fall back to context
  const isProcessing = isProcessingProp ?? processingContext?.isProcessing ?? false;

  // Build orbiting hashes from prop or context
  const orbitingHashes = useMemo(() => {
    // If prop provided, use it
    if (orbitingHashesProp !== undefined) {
      return orbitingHashesProp;
    }
    // Otherwise derive from vault context
    if (vaultContext?.vaultedItems) {
      return vaultContext.vaultedItems.map(item => ({
        hash: item.hash,
        algorithm: item.metadata?.algorithm ?? 'SHA-256',
        encrypted: !!item.encrypted,
      }));
    }
    return [];
  }, [orbitingHashesProp, vaultContext?.vaultedItems]);

  // Generate orbital particles
  const orbitalParticles = useMemo<OrbitalParticle[]>(() => {
    const particles: OrbitalParticle[] = [];
    const particleCount = 30;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        id: i,
        angle: (i / particleCount) * 360,
        radius: 80 + Math.random() * 60,
        speed: 2 + Math.random() * 3,
        size: 2 + Math.random() * 3,
        opacity: 0.4 + Math.random() * 0.6,
        color: i % 3 === 0 ? '#a855f7' : i % 3 === 1 ? '#ec4899' : '#3b82f6',
      });
    }

    return particles;
  }, []);

  // Generate orbiting hash configurations
  const orbitingHashConfigs = useMemo<OrbitingHashConfig[]>(() => {
    if (orbitingHashes.length === 0) return [];

    // Limit to 8 hashes for visual clarity, show newest
    const limitedHashes = orbitingHashes.slice(0, 8);

    return limitedHashes.map((hashData, index) => ({
      index,
      hash: hashData.hash,
      algorithm: hashData.algorithm,
      encrypted: hashData.encrypted ?? false,
      angle: (index / limitedHashes.length) * 360,
      orbitRadius: 0.38 + (index % 2) * 0.08, // Alternate between two orbital rings
      speed: 45 + (index % 3) * 10, // Varying speeds for visual interest
      opacity: 0.7 - (index * 0.05), // Slightly fade older hashes
    }));
  }, [orbitingHashes]);

  // Generate data fragments when processing starts
  useEffect(() => {
    if (isProcessing) {
      const chars = '01ABCDEFabcdef#@$%&*'.split('');
      const fragments: DataFragment[] = [];

      for (let i = 0; i < 20; i++) {
        fragments.push({
          id: i,
          startAngle: Math.random() * 360,
          startRadius: 150 + Math.random() * 50,
          char: chars[Math.floor(Math.random() * chars.length)] ?? '0',
          delay: i * 0.1,
        });
      }

      setDataFragments(fragments);
    } else {
      setDataFragments([]);
    }
  }, [isProcessing]);

  // Calculate glow intensity based on props
  const glowIntensity = 0.3 + intensity * 0.7;
  const rotationSpeed = 20 - intensity * 10; // Faster when higher intensity

  return (
    <div
      className="relative flex items-center justify-center"
      style={{
        width: size,
        height: size,
      }}
    >
      {/* Outer glow layer */}
      <div
        className="absolute rounded-full"
        style={{
          width: size * 1.2,
          height: size * 1.2,
          background: `radial-gradient(circle, rgba(138, 43, 226, ${glowIntensity * 0.2}) 0%, transparent 70%)`,
          animation: 'pulse-glow 4s ease-in-out infinite',
        }}
      />

      {/* Event Horizon */}
      <div
        className="absolute rounded-full"
        style={{
          width: size,
          height: size,
          background: `
            radial-gradient(circle,
              transparent 25%,
              rgba(138, 43, 226, ${glowIntensity * 0.3}) 40%,
              rgba(75, 0, 130, ${glowIntensity * 0.6}) 60%,
              rgba(30, 0, 50, 0.9) 80%,
              black 100%
            )
          `,
          boxShadow: `
            0 0 ${60 * glowIntensity}px rgba(138, 43, 226, ${glowIntensity * 0.5}),
            0 0 ${120 * glowIntensity}px rgba(138, 43, 226, ${glowIntensity * 0.3}),
            inset 0 0 ${80 * glowIntensity}px rgba(138, 43, 226, ${glowIntensity * 0.3})
          `,
          animation: isProcessing ? `spin-slow ${rotationSpeed}s linear infinite` : undefined,
        }}
      />

      {/* Accretion Disk - Primary */}
      <div
        className="absolute rounded-full"
        style={{
          width: size * 0.95,
          height: size * 0.95,
          background: `conic-gradient(
            from 0deg,
            transparent,
            rgba(255, 105, 180, ${glowIntensity * 0.4}),
            rgba(138, 43, 226, ${glowIntensity * 0.3}),
            transparent,
            rgba(59, 130, 246, ${glowIntensity * 0.3}),
            rgba(236, 72, 153, ${glowIntensity * 0.4}),
            transparent
          )`,
          filter: 'blur(3px)',
          animation: `spin-slower ${rotationSpeed * 1.5}s linear infinite reverse`,
        }}
      />

      {/* Accretion Disk - Secondary (faster) */}
      <div
        className="absolute rounded-full"
        style={{
          width: size * 0.85,
          height: size * 0.85,
          background: `conic-gradient(
            from 180deg,
            transparent,
            rgba(168, 85, 247, ${glowIntensity * 0.2}),
            transparent,
            rgba(236, 72, 153, ${glowIntensity * 0.2}),
            transparent
          )`,
          filter: 'blur(2px)',
          animation: `spin-slow ${rotationSpeed}s linear infinite`,
        }}
      />

      {/* Inner dark region */}
      <div
        className="absolute rounded-full bg-black"
        style={{
          width: size * 0.35,
          height: size * 0.35,
          boxShadow: `
            0 0 ${30 * glowIntensity}px rgba(0, 0, 0, 0.8),
            inset 0 0 ${20 * glowIntensity}px rgba(138, 43, 226, ${glowIntensity * 0.3})
          `,
        }}
      />

      {/* Singularity */}
      <div
        className="absolute rounded-full"
        style={{
          width: size * 0.15,
          height: size * 0.15,
          background: 'radial-gradient(circle, rgba(0, 0, 0, 1) 0%, rgba(20, 0, 40, 1) 100%)',
          boxShadow: `
            0 0 ${40 * glowIntensity}px rgba(138, 43, 226, ${glowIntensity * 0.8}),
            0 0 ${80 * glowIntensity}px rgba(138, 43, 226, ${glowIntensity * 0.4}),
            0 0 ${120 * glowIntensity}px rgba(138, 43, 226, ${glowIntensity * 0.2})
          `,
          animation: 'singularity-pulse 2s ease-in-out infinite',
        }}
      />

      {/* Orbital Particles (always visible, more active when processing) */}
      {orbitalParticles.map((particle) => (
        <div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            width: particle.size,
            height: particle.size,
            backgroundColor: particle.color,
            opacity: isProcessing ? particle.opacity : particle.opacity * 0.3,
            left: '50%',
            top: '50%',
            transform: `rotate(${particle.angle}deg) translateX(${particle.radius}px)`,
            boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`,
            animation: isProcessing
              ? `orbit-particle ${particle.speed}s linear infinite`
              : `orbit-particle ${particle.speed * 3}s linear infinite`,
          }}
        />
      ))}

      {/* Processing Particles - Spiral inward */}
      {isProcessing &&
        [...Array(20)].map((_, i) => (
          <div
            key={`process-${i}`}
            className="absolute rounded-full bg-purple-400"
            style={{
              width: 4,
              height: 4,
              left: '50%',
              top: '50%',
              boxShadow: '0 0 8px rgba(168, 85, 247, 0.8)',
              animation: `spiral-in 2s ease-in infinite`,
              animationDelay: `${i * 0.1}s`,
            }}
          />
        ))}

      {/* Data Fragments being sucked in */}
      {isProcessing &&
        dataFragments.map((fragment) => (
          <div
            key={`fragment-${fragment.id}`}
            className="absolute text-purple-300 font-mono text-xs pointer-events-none"
            style={{
              left: '50%',
              top: '50%',
              animation: `suck-in 2s ease-in infinite`,
              animationDelay: `${fragment.delay}s`,
              transform: `rotate(${fragment.startAngle}deg) translateX(${fragment.startRadius}px)`,
              textShadow: '0 0 10px rgba(168, 85, 247, 0.8)',
            }}
          >
            {fragment.char}
          </div>
        ))}

      {/* Gravitational lensing effect ring */}
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: size * 0.55,
          height: size * 0.55,
          border: `1px solid rgba(138, 43, 226, ${glowIntensity * 0.3})`,
          animation: 'lensing-ring 3s ease-in-out infinite',
        }}
      />

      {/* Orbiting Hashes from Vault */}
      {orbitingHashConfigs.length > 0 && (
        <div
          className="absolute pointer-events-none"
          style={{
            width: size,
            height: size,
            animation: 'orbit-hashes 60s linear infinite',
          }}
        >
          {orbitingHashConfigs.map((config) => {
            const truncatedHash = `${config.hash.slice(0, 4)}...${config.hash.slice(-4)}`;
            const orbitSize = size * config.orbitRadius;
            const colorByAlgo = config.encrypted
              ? 'rgba(34, 211, 238, 0.9)' // cyan for encrypted
              : config.algorithm === 'SHA-512'
              ? 'rgba(96, 165, 250, 0.9)' // blue
              : config.algorithm === 'SHA-384'
              ? 'rgba(168, 85, 247, 0.9)' // purple
              : 'rgba(74, 222, 128, 0.9)'; // green for SHA-256

            return (
              <div
                key={`hash-orbit-${config.index}`}
                className="absolute left-1/2 top-1/2 font-mono pointer-events-none"
                style={{
                  transform: `translate(-50%, -50%) rotate(${config.angle}deg) translateX(${orbitSize}px)`,
                  animation: `orbit-hash-individual ${config.speed}s linear infinite`,
                  opacity: config.opacity,
                }}
              >
                <div
                  className="px-2 py-0.5 rounded-full text-xs whitespace-nowrap"
                  style={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    border: `1px solid ${colorByAlgo}`,
                    color: colorByAlgo,
                    textShadow: `0 0 8px ${colorByAlgo}`,
                    boxShadow: `0 0 10px ${colorByAlgo.replace('0.9', '0.3')}`,
                    transform: `rotate(-${config.angle}deg)`, // Counter-rotate to keep text readable
                  }}
                >
                  {truncatedHash}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Hash count indicator */}
      {orbitingHashes.length > 0 && (
        <div
          className="absolute text-xs font-mono pointer-events-none"
          style={{
            bottom: size * 0.08,
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'rgba(168, 85, 247, 0.7)',
            textShadow: '0 0 10px rgba(168, 85, 247, 0.5)',
          }}
        >
          {orbitingHashes.length} hash{orbitingHashes.length !== 1 ? 'es' : ''} in orbit
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes spin-slower {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }

        @keyframes pulse-glow {
          0%, 100% {
            transform: scale(1);
            opacity: 0.5;
          }
          50% {
            transform: scale(1.1);
            opacity: 1;
          }
        }

        @keyframes singularity-pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow:
              0 0 40px rgba(138, 43, 226, 0.8),
              0 0 80px rgba(138, 43, 226, 0.4);
          }
          50% {
            transform: scale(1.1);
            box-shadow:
              0 0 60px rgba(138, 43, 226, 1),
              0 0 120px rgba(138, 43, 226, 0.6);
          }
        }

        @keyframes orbit-particle {
          from {
            transform: rotate(0deg) translateX(var(--radius, 100px)) rotate(0deg);
          }
          to {
            transform: rotate(360deg) translateX(var(--radius, 100px)) rotate(-360deg);
          }
        }

        @keyframes spiral-in {
          0% {
            transform: translate(-50%, -50%) rotate(0deg) translateX(150px);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) rotate(720deg) translateX(0px);
            opacity: 0;
          }
        }

        @keyframes suck-in {
          0% {
            opacity: 1;
            transform: rotate(var(--start-angle, 0deg)) translateX(var(--start-radius, 150px)) scale(1);
          }
          100% {
            opacity: 0;
            transform: rotate(calc(var(--start-angle, 0deg) + 360deg)) translateX(0px) scale(0);
          }
        }

        @keyframes lensing-ring {
          0%, 100% {
            transform: scale(1);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.6;
          }
        }

        @keyframes orbit-hashes {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes orbit-hash-individual {
          from {
            transform: translate(-50%, -50%) rotate(0deg) translateX(var(--orbit-radius, 120px));
          }
          to {
            transform: translate(-50%, -50%) rotate(360deg) translateX(var(--orbit-radius, 120px));
          }
        }
      `}</style>
    </div>
  );
};

export default BlackHole;
